import { gql, useQuery } from '@apollo/client'
import uniqBy from 'lodash/uniqBy'
import { useMemo } from 'react'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  CurrentUserFragmentDoc,
  EventFilter,
  EventListFragmentDoc,
  EventsQuery,
  EventsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims } = useCurrentUser()

  const { isFirstLoadRef } = useGetNewestData<
    EventsQuery,
    EventsQueryVariables
  >({
    query: FETCH_NEW_EVENT,
    variables: {
      artistId: config.artistId,
      filter: EventFilter.All,
      isStandalone: config.applicationType === 'standalone',
    },
    fields: [
      {
        fieldName: 'events',
        fragmentName: 'EventList',
        fragment: EventListFragmentDoc,
      },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, loading, fetchMore } = useQuery<
    EventsQuery,
    EventsQueryVariables
  >(QUERY, {
    variables: {
      artistId: config.artistId,
      filter: EventFilter.All,
      isStandalone: config.applicationType === 'standalone',
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      isFirstLoadRef.current = false
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  // fixed issue duplicate events on graphql cached
  const events = useMemo(() => {
    if (!data?.events) {
      return null
    }
    return {
      ...data.events,
      edges: uniqBy(data.events.edges, 'node.id'),
    }
  }, [data?.events])

  return {
    state: {
      user: data?.user ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      events,
      loading,
      hasNextPage: !!data?.events?.pageInfo.hasNextPage,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            filter: EventFilter.All,
            pageInput: {
              cursor: data?.events?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query Events(
    $artistId: ID
    $filter: EventFilter!
    $pageInput: PageInput
    $isStandalone: Boolean!
  ) {
    user {
      ...CurrentUser
    }
    artist(id: $artistId) @include(if: $isStandalone) {
      ...ArtistBottomTab
      currentLive {
        id
      }
    }
    events(filter: $filter, pageInput: $pageInput) {
      edges {
        node {
          ... on Event {
            ...EventList
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${EventListFragmentDoc}
`

const FETCH_NEW_EVENT = gql`
  query FetchNewEvents(
    $artistId: ID
    $filter: EventFilter!
    $pageInput: PageInput
    $isStandalone: Boolean!
  ) {
    artist(id: $artistId) @include(if: $isStandalone) {
      ...ArtistBottomTab
      currentLive {
        id
      }
    }
    events(filter: $filter, pageInput: $pageInput) {
      edges {
        node {
          ... on Event {
            ...EventList
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${EventListFragmentDoc}
`
